'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

const FinalConversion = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const urgencyFactors = [
    {
      icon: 'ClockIcon',
      title: 'Time-Sensitive',
      description: 'Every hour counts in neonatal brain injury. Early intervention with REVIVE maximizes outcomes.',
    },
    {
      icon: 'HeartIcon',
      title: 'Lives at Stake',
      description: '150,000+ newborns face HIE annually in India. Your NICU could be saving more lives with REVIVE.',
    },
    {
      icon: 'ChartBarIcon',
      title: 'Proven Results',
      description: '400+ neonates successfully treated. Join leading Indian hospitals already using REVIVE.',
    },
  ];

  const contactMethods = [
    {
      icon: 'PhoneIcon',
      title: 'Call Clinical Team',
      value: '(+91) 9446065047',
      href: 'tel:+919446065047',
      description: 'Available 24/7 for urgent inquiries',
    },
    {
      icon: 'EnvelopeIcon',
      title: 'Email Clinical Team',
      value: 'contact@sensivisionhealth.com',
      href: 'mailto:contact@sensivisionhealth.com',
      description: 'Response within 4 business hours',
    },
    {
      icon: 'MapPinIcon',
      title: 'Corporate Headquarters',
      value: 'Sensivision Health Technologies Pvt. Ltd, Bengaluru, Karnataka, India',
      href: '#location',
      description: 'Visit our office',
    },
  ];

  return (
    <section id="request-demo" className="py-24 bg-gradient-to-br from-primary via-primary/95 to-secondary relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.1),transparent_50%)]" />
      </div>

      <div className="max-w-[1200px] mx-auto px-6 relative z-10">
        <div className="text-center mb-16 text-primary-foreground">
          <h2 className="lg:text-5xl font-bold font-heading mb-6 text-[32px]">
            Every Day Matters in Neonatal Treatment
          </h2>

          <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
            Reach out to our clinical team to explore how REVIVE can transform outcomes in your NICU.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {urgencyFactors.map((point, index) => (
              <div key={index} className="bg-primary-foreground/10 backdrop-blur-sm rounded-lg p-6">
                <div className="p-3 bg-accent/20 rounded-lg inline-flex mb-4">
                  <Icon name={point.icon as any} size={32} className="text-accent-foreground" />
                </div>
                <h3 className="text-lg font-bold font-heading mb-2">{point.title}</h3>
                <p className="text-sm opacity-80">{point.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div id="contact" className="bg-card rounded-lg shadow-card p-8">
          <h3 className="text-2xl font-bold font-heading text-foreground mb-8 text-center">
            Connect With Our Clinical Team
          </h3>

          <div className="grid md:grid-cols-3 gap-6">
            {contactMethods.map((method, index) => (
              <a
                key={index}
                href={method.href}
                className="text-center p-6 rounded-lg hover:bg-muted transition-all duration-250 ease-out group"
              >
                <div className="p-4 bg-primary/10 rounded-lg inline-flex mb-4 group-hover:bg-primary/20 transition-colors">
                  <Icon name={method.icon as any} size={32} className="text-primary" />
                </div>
                <h4 className="font-bold text-foreground mb-2">{method.title}</h4>
                <p className="text-lg font-semibold text-primary mb-1 group-hover:underline">{method.value}</p>
                <p className="text-sm text-muted-foreground">{method.description}</p>
              </a>
            ))}
          </div>

          {/* Google Maps Location */}
          <div className="mt-8" id="location">
            <h4 className="text-xl font-bold font-heading text-foreground mb-4 text-center">
              Visit Our Office
            </h4>
            <div className="w-full h-[400px] rounded-lg overflow-hidden shadow-md">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.826789!2d77.5923!3d13.0358!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae17d0e5c6f0a5%3A0x1234567890abcdef!2sSanjaynagar%2C%20Bengaluru%2C%20Karnataka!5e0!3m2!1sen!2sin!4v1735729391000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Sensivision Health Technologies Office Location"
              />
            </div>
          </div>
        </div>

        <div className="mt-12 bg-surface rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="p-2 bg-success/10 rounded-lg flex-shrink-0">
              <Icon name="ShieldCheckIcon" size={24} className="text-success" />
            </div>
            <div>
              <h4 className="font-bold text-foreground mb-2">Privacy & HIPAA Compliance Assurance</h4>
              <p className="text-sm text-muted-foreground">
                All communications and data handling comply with HIPAA regulations. Your institutional information and patient data are protected with enterprise-grade security. We maintain comprehensive Business Associate Agreements with all hospital partners.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalConversion;